/**
 * SendCalendlyLinkEditor
 *
 * Workflow node that generates a personalized Calendly scheduling link and
 * sends it to the visitor — no HTTP node configuration required.
 *
 * Requires:
 *   1. Calendly connected (Integrations → Calendly) with a default event type set
 *   2. Visitor email collected via an Ask Question node BEFORE this node
 *
 * The node calls POST /api/admin/Calendly/SchedulingLink and stores the returned
 * bookingUrl in a workflow variable. By default it also sends the link in chat
 * immediately (no separate Send Message node needed).
 *
 * Execution: handled in velaro-messaging WorkflowJobRunner.
 *
 * Fallback: if the messaging engine does not yet support this node type, replace with:
 *   HTTP Request → POST /api/admin/Calendly/SchedulingLink → read <<bookingUrl>> result
 *   → Send Message: "Here's your booking link: <<bookingUrl>>"
 */
import * as React from "react";
import { Link } from "react-router-dom";
import { FormGroup, ToggleGroup } from "../RightAsideHelpers";
import { WorkflowNode } from "../../types";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function SendCalendlyLinkEditor({ node, onUpdate }: Props) {
  const emailVar      = node.calendlyLinkEmailVar   ?? "visitorEmail";
  const nameVar       = node.calendlyLinkNameVar    ?? "visitorName";
  const resultVar     = node.calendlyLinkResultVar  ?? "bookingUrl";
  const eventTypeUri  = node.calendlyLinkEventTypeUri ?? "";
  const sendInChat    = node.calendlyLinkSendInChat ?? true;
  const chatMessage   = node.calendlyLinkChatMessage ?? "Here's your personalized booking link — pick any time that works: <<bookingUrl>>";

  return (
    <>
      {/* Info banner */}
      <div className="mb-4 rounded-md bg-[#006BFF]/10 border border-[#006BFF]/20 px-3 py-2.5 text-xs text-blue-900">
        <p className="font-semibold mb-1">How this node works</p>
        <p className="leading-relaxed">
          Velaro calls your Calendly account to generate a <strong>single-use scheduling link</strong>
          {" "}for this visitor (preventing link sharing). The visitor clicks it and picks their own
          time from your real Calendly availability — including buffer times, limits, and any
          Calendly automations you've configured (Salesforce sync, Zoom links, HubSpot updates).
        </p>
        <p className="mt-1.5">
          <strong>Pre-requisite:</strong> Configure Calendly in{" "}
          <Link to="/integrations/calendly" className="underline font-medium">
            Integrations → Calendly
          </Link>{" "}
          with a default event type selected.
        </p>
      </div>

      {/* Visitor info */}
      <div className="mb-4 rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5">
        <p className="text-xs font-semibold text-slate-600 mb-2">Visitor info — from workflow variables</p>

        <FormGroup label="Visitor email variable">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={emailVar}
            onChange={e => onUpdate({ calendlyLinkEmailVar: e.currentTarget.value })}
            placeholder="visitorEmail"
          />
          <span className="text-xs text-slate-400 block mt-1">
            Used to pre-fill the visitor's email on the Calendly booking page.
          </span>
        </FormGroup>

        <FormGroup label="Visitor name variable (optional)">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={nameVar}
            onChange={e => onUpdate({ calendlyLinkNameVar: e.currentTarget.value })}
            placeholder="visitorName"
          />
        </FormGroup>
      </div>

      {/* Event type override */}
      <div className="mb-4 rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5">
        <p className="text-xs font-semibold text-slate-600 mb-1">Event type</p>
        <p className="text-xs text-slate-500 mb-3">
          Leave blank to use the <strong>default event type</strong> from your Calendly config.
          Override with a specific event type URI to direct visitors to a particular meeting type
          (e.g. "30 Minute Demo" vs "Support Call").
        </p>

        <FormGroup label="Event type URI (optional override)">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={eventTypeUri}
            onChange={e => onUpdate({ calendlyLinkEventTypeUri: e.currentTarget.value })}
            placeholder="https://api.calendly.com/event_types/XXXXXXXX  (blank = use default)"
          />
          <span className="text-xs text-slate-400 block mt-1">
            Find the URI in{" "}
            <Link to="/integrations/calendly" className="text-blue-500 underline">
              Integrations → Calendly → Browse event types
            </Link>.
          </span>
        </FormGroup>
      </div>

      {/* Auto-send in chat */}
      <ToggleGroup
        label="Send link in chat automatically"
        toggled={sendInChat}
        changeToggle={() => onUpdate({ calendlyLinkSendInChat: !sendInChat })}
        helpText="When on, the booking link is sent to the visitor immediately after it's created — no separate Send Message node needed."
      />

      {sendInChat && (
        <FormGroup label="Chat message template">
          <textarea
            rows={3}
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={chatMessage}
            onChange={e => onUpdate({ calendlyLinkChatMessage: e.currentTarget.value })}
            placeholder="Here's your booking link: <<bookingUrl>>"
          />
          <span className="text-xs text-slate-400 block mt-1">
            Use <code className="bg-slate-100 px-1 rounded">{`<<${resultVar}>>`}</code> where you want the link to appear.
          </span>
        </FormGroup>
      )}

      {/* Result variable */}
      <div className="mt-4 rounded-md bg-green-50 border border-green-200 px-3 py-2.5">
        <p className="text-xs font-semibold text-green-800 mb-1">Result variable</p>

        <FormGroup label="Save booking URL as variable">
          <input
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={resultVar}
            onChange={e => onUpdate({ calendlyLinkResultVar: e.currentTarget.value })}
            placeholder="bookingUrl"
          />
          <span className="text-xs text-slate-400 block mt-1">
            The generated Calendly link is stored here for use in follow-up messages or CRM pushes.
          </span>
        </FormGroup>
      </div>

      {/* Calendly advantages reminder */}
      <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800">
        <p className="font-semibold mb-1">Calendly handles the rest automatically</p>
        <ul className="list-disc list-inside space-y-0.5 leading-relaxed">
          <li>Calendar invite sent to visitor and organizer</li>
          <li>Reminder emails 1 hour before and 1 day before (configurable in Calendly)</li>
          <li>CRM sync (Salesforce, HubSpot) if configured in Calendly</li>
          <li>Zoom / Teams link auto-generated if connected in Calendly</li>
        </ul>
      </div>
    </>
  );
}
